import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  DownloadOutlined,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";

import SearchSelect, { OptionItem } from "../../atoms/SearchSelect/SearchSelect";
import SelectField from "../../atoms/SelectField/SelectField";
import { findAllAcoesQualificacao, findAllTurmasAcao } from "../../../API/apiAcoes";
import { findRelatorioPesquisaRespostas } from "../../../API/apiRelatoriosTurma";
import { AcaoQualificacao, TurmaAcao } from "../../../types/acao";
import { RespostaPesquisaItem } from "../../../types/relatoriosTurma";
import { exportXLSX } from "../../../utils/exporters/exportXLSX";
import { formatarCpf, formatarDatetimeCompletoBr } from "../../../utils/Masks";

import styles from "./ModelosPesquisa.module.css";
import stylesFilter from "../../../styles/Filter.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesTable from "../../../styles/Table.module.css";
import stylesButton from "../../../styles/Button.module.css";
import stylesGeneral from "../../../styles/General.module.css";

const perPageOptions = [{ nome: "10" }, { nome: "20" }, { nome: "30" }];
const escalaLabels: Record<string, string> = {
  "1": "Pessimo",
  "2": "Ruim",
  "3": "Regular",
  "4": "Bom",
  "5": "Otimo",
};

const columnsParticipanteBase = [
  { label: "Nome", key: "nome" },
  { label: "CPF", key: "cpf" },
  { label: "E-mail", key: "email" },
  { label: "Municipio", key: "municipio" },
  { label: "Atuacao", key: "atuacao" },
  { label: "Lotacao", key: "lotacao" },
  { label: "Vinculo", key: "vinculo" },
  { label: "Funcao", key: "funcao" },
  { label: "Superintendencia", key: "superintendencia" },
  { label: "Unidade", key: "unidade" },
  { label: "Diretoria Regional", key: "diretoria_regional" },
  { label: "Diretoria Interna", key: "diretoria_interna" },
  { label: "Orgao de Atuacao", key: "orgao_atuacao" },
  { label: "Instituicao", key: "instituicao" },
  { label: "Ambito de Atuacao", key: "ambito_atuacao" },
];

const formatRespostaPesquisa = (resposta: {
  tipo?: string;
  resposta?: string | number | Array<string | number> | null;
  resposta_texto?: string | null;
}) => {
  if (resposta.tipo === "escala") {
    const key = String(resposta.resposta ?? resposta.resposta_texto ?? "").trim();
    return escalaLabels[key] || resposta.resposta_texto || key || "-";
  }

  return resposta.resposta_texto || "-";
};

const getParticipantBaseData = (item: {
  participante: {
    nome: string;
    cpf: string;
    email: string;
    dados_profissionais?: any;
  };
}) => {
  const professional = item.participante?.dados_profissionais;

  return {
    nome: item.participante?.nome ?? "",
    cpf: formatarCpf(item.participante?.cpf ?? ""),
    email: item.participante?.email ?? "",
    municipio: professional?.municipio_nome ?? "",
    atuacao: professional?.orgao_nome ?? professional?.lotacao_nome ?? professional?.instituicao_nome ?? "",
    lotacao: professional?.lotacao_nome ?? "",
    vinculo: professional?.vinculo_nome ?? "",
    funcao: professional?.funcao_nome ?? "",
    superintendencia: professional?.superintendencia_nome ?? "",
    unidade: professional?.unidade_nome ?? "",
    diretoria_regional: professional?.diretoria_regional_nome ?? "",
    diretoria_interna: professional?.diretoria_interna_nome ?? "",
    orgao_atuacao: professional?.orgao_nome ?? "",
    instituicao: professional?.instituicao_nome ?? "",
    ambito_atuacao: professional?.ambito_nome ?? "",
  };
};

const buildPagination = (
  currentPage: number,
  totalPages: number,
  perPage: number,
  setCurrentPage: (page: number) => void,
  setPerPage: (value: number) => void,
) => (
  <div className={`${stylesTable.containerPagination} ${stylesGeneral.mt4}`}>
    <div className={stylesTable.qtdItems}>
      <div className={stylesTable.labelSelect}>Resultados por pagina</div>
      <SelectField
        label=""
        value={perPage.toString()}
        onChange={(value) => {
          setPerPage(Number(value));
          setCurrentPage(1);
        }}
        options={perPageOptions}
        disableClearable
        disableTyping
      />
    </div>

    <div className={stylesTable.pagination}>
      <IconButton
        className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(1)}
      >
        <KeyboardDoubleArrowLeftOutlined />
      </IconButton>
      <IconButton
        className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        <KeyboardArrowLeftOutlined />
      </IconButton>
      <span className={stylesTable.pages}>
        Pagina {currentPage} de {totalPages}
      </span>
      <IconButton
        className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        <KeyboardArrowRightOutlined />
      </IconButton>
      <IconButton
        className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(totalPages)}
      >
        <KeyboardDoubleArrowRightOutlined />
      </IconButton>
    </div>

    <div className={stylesTable.setNumberPage}>
      <div className={stylesTable.labelSelect}>Ir para a pagina</div>
      <SelectField
        label=""
        value={currentPage.toString()}
        onChange={(value) => setCurrentPage(Number(value))}
        options={Array.from({ length: totalPages }, (_, index) => ({ nome: String(index + 1) }))}
        disableTyping
      />
    </div>
  </div>
);

const RelatorioRespostasPesquisa = () => {
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingTurmas, setLoadingTurmas] = useState(false);
  const [loadingRespostas, setLoadingRespostas] = useState(false);
  const [loadingExportRespostas, setLoadingExportRespostas] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [acoes, setAcoes] = useState<AcaoQualificacao[]>([]);
  const [turmas, setTurmas] = useState<TurmaAcao[]>([]);
  const [acaoId, setAcaoId] = useState<number | null>(null);
  const [turmaId, setTurmaId] = useState<number | null>(null);
  const [respostas, setRespostas] = useState<RespostaPesquisaItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [respostaSelecionada, setRespostaSelecionada] = useState<RespostaPesquisaItem | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const timer = msgError ? setTimeout(() => setMsgError(null), 5000) : undefined;
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [msgError]);

  useEffect(() => {
    const timer = msgSuccess ? setTimeout(() => setMsgSuccess(null), 5000) : undefined;
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [msgSuccess]);

  useEffect(() => {
    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAllAcoesQualificacao(1, 999999);
        setAcoes(response.acoes || []);
      } catch {
        setMsgError("Nao foi possivel carregar as acoes.");
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, []);

  useEffect(() => {
    const loadTurmas = async () => {
      if (!acaoId) {
        setTurmas([]);
        setTurmaId(null);
        return;
      }

      try {
        setLoadingTurmas(true);
        const result = await findAllTurmasAcao(String(acaoId));
        setTurmas(result || []);
      } catch {
        setMsgError("Nao foi possivel carregar as turmas da acao.");
        setTurmas([]);
      } finally {
        setLoadingTurmas(false);
      }
    };

    setTurmaId(null);
    setRespostas([]);
    void loadTurmas();
  }, [acaoId]);

  useEffect(() => {
    if (!turmaId) {
      setRespostas([]);
      setTotalPages(1);
      return;
    }

    const loadRespostas = async () => {
      try {
        setLoadingRespostas(true);
        const response = await findRelatorioPesquisaRespostas(turmaId, {
          page: currentPage,
          perPage,
          search: search.trim() || undefined,
        });
        setRespostas(response.data || []);
        setTotalPages(response.pagination?.last_page || 1);
      } catch {
        setMsgError("Nao foi possivel carregar o relatorio de respostas da pesquisa.");
      } finally {
        setLoadingRespostas(false);
      }
    };

    void loadRespostas();
  }, [turmaId, currentPage, perPage, search]);

  const acaoOptions = useMemo<OptionItem[]>(
    () => acoes.map((acao) => ({ id: acao.id, nome: acao.nome })),
    [acoes],
  );

  const turmaOptions = useMemo<OptionItem[]>(
    () => turmas.map((turma) => ({ id: turma.id, nome: turma.nome })),
    [turmas],
  );

  const handleExport = async () => {
    if (!turmaId) return;

    try {
      setLoadingExportRespostas(true);
      const response = await findRelatorioPesquisaRespostas(turmaId, {
        page: 1,
        perPage: 999999,
        search: search.trim() || undefined,
      });

      const questions = Array.from(
        new Map(
          (response.data || [])
            .flatMap((item) => item.respostas || [])
            .sort((a, b) => a.ordem - b.ordem)
            .map((resposta) => [
              resposta.pergunta_id,
              { label: resposta.pergunta, key: `pergunta_${resposta.pergunta_id}` },
            ]),
        ).values(),
      );

      const rows = (response.data || []).map((item) => {
        const answers = (item.respostas || []).reduce<Record<string, string>>((acc, resposta) => {
          acc[`pergunta_${resposta.pergunta_id}`] = formatRespostaPesquisa(resposta);
          return acc;
        }, {});

        return {
          ...getParticipantBaseData(item),
          ...answers,
        };
      });

      exportXLSX([...columnsParticipanteBase, ...questions], rows, `respostas_pesquisa_turma_${turmaId}.xlsx`);
      setMsgSuccess("Exportacao do relatorio de respostas iniciada.");
    } catch {
      setMsgError("Nao foi possivel exportar o relatorio de respostas.");
    } finally {
      setLoadingExportRespostas(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.title}>Respostas da Pesquisa</div>

      <div className={stylesGeneral.mt4}>
        <div className={stylesFilter.container}>
          <div className={stylesFilter.grid12}>
            <div className={stylesFilter.inputGroup6}>
              <label className={stylesInput.titleInput}>Ação</label>
              <SearchSelect
                label=""
                options={acaoOptions}
                value={acaoId}
                onChange={(id) => {
                  setAcaoId(id);
                  setCurrentPage(1);
                  setSearch("");
                }}
                disabled={loadingAcoes}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>

            <div className={stylesFilter.inputGroup6}>
              <label className={stylesInput.titleInput}>Turma</label>
              <SearchSelect
                label=""
                options={turmaOptions}
                value={turmaId}
                onChange={(id) => {
                  setTurmaId(id);
                  setCurrentPage(1);
                }}
                disabled={!acaoId || loadingTurmas}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>
          </div>
        </div>
      </div>

      {!turmaId ? (
        <div className={stylesGeneral.mt4}>
          <Alert severity="info">Selecione uma acao e uma turma para visualizar os relatorios disponiveis.</Alert>
        </div>
      ) : (
        <section className={stylesGeneral.mt4}>

          <div className={styles.sectionToolbar}>
            <div className={styles.searchBox}>
              <label className={stylesInput.titleInput}>Pesquisar participante</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Nome, CPF ou e-mail"
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>
            <Button
              variant="contained"
              className={`${stylesButton.buttonSquarePrimary} ${loadingExportRespostas ? styles.disabledAction : ""}`}
              onClick={handleExport}
              startIcon={<DownloadOutlined />}
              disabled={loadingExportRespostas}
            >
              {loadingExportRespostas ? "Exportando..." : "Exportar XLS"}
            </Button>
          </div>

          <TableContainer className={stylesTable.tableContainer}>
            {loadingRespostas ? (
              <div className={styles.loader}>
                <CircularProgress />
              </div>
            ) : (
              <Table size="small">
                <TableHead className={stylesTable.tableHead}>
                  <TableRow>
                    <TableCell padding="none" className={stylesTable.tableHeadRow}>Nome</TableCell>
                    <TableCell padding="none" className={stylesTable.tableHeadRow}>CPF</TableCell>
                    <TableCell padding="none" className={stylesTable.tableHeadRow}>Ambito de Atuacao</TableCell>
                    <TableCell padding="none" className={stylesTable.tableHeadRow}>Municipio</TableCell>
                    <TableCell padding="none" className={stylesTable.tableHeadRow}>Lotacao / Orgao</TableCell>
                    <TableCell padding="none" className={stylesTable.tableHeadRow} align="center">Acoes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {respostas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" className={stylesTable.textXs}>
                        Nenhum registro encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    respostas.map((item, index) => (
                      <TableRow
                        key={item.id}
                        className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}
                      >
                        <TableCell padding="none" className={stylesTable.textXs}>{item.participante?.nome}</TableCell>
                        <TableCell padding="none" className={stylesTable.textXs}>{formatarCpf(item.participante?.cpf)}</TableCell>
                        <TableCell padding="none" className={stylesTable.textXs}>{item.participante?.dados_profissionais?.ambito_nome || "-"}</TableCell>
                        <TableCell padding="none" className={stylesTable.textXs}>{item.participante?.dados_profissionais?.municipio_nome || "-"}</TableCell>
                        <TableCell padding="none" className={stylesTable.textXs}>
                          {item.participante?.dados_profissionais?.lotacao_nome || item.participante?.dados_profissionais?.orgao_nome || "-"}
                        </TableCell>
                        <TableCell padding="none" align="center">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => {
                              setRespostaSelecionada(item);
                              setOpenDialog(true);
                            }}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {buildPagination(currentPage, totalPages, perPage, setCurrentPage, setPerPage)}
        </section>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Respostas do participante</DialogTitle>
        <DialogContent dividers>
          {respostaSelecionada ? (
            <div className={styles.answerList}>
              <div className={styles.answerParticipant}>
                <strong>{respostaSelecionada.participante?.nome}</strong>
                <span>{formatarCpf(respostaSelecionada.participante?.cpf)}</span>
                <span>{respostaSelecionada.participante?.email}</span>
                <span>Preenchida em: {formatarDatetimeCompletoBr(respostaSelecionada.preenchida_em) || "-"}</span>
              </div>

              {(respostaSelecionada.respostas || [])
                .slice()
                .sort((a, b) => a.ordem - b.ordem)
                .map((resposta) => (
                  <div key={resposta.id} className={styles.answerCard}>
                    <div className={styles.answerQuestion}>
                      {resposta.ordem}. {resposta.pergunta}
                    </div>
                    <div className={styles.answerMeta}>
                      Tipo: {resposta.tipo} {resposta.obrigatoria ? " | Obrigatoria" : " | Opcional"}
                    </div>
                    <div className={styles.answerValue}>{formatRespostaPesquisa(resposta)}</div>
                  </div>
                ))}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" className={stylesButton.buttonSquareSecondary} onClick={() => setOpenDialog(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RelatorioRespostasPesquisa;
